import React, { useState } from "react";
import { CheckCircle2, RotateCcw, Clock, Star } from "lucide-react";

const CoffeeMeetingModal = ({ onClose }) => {
  // Local state for the checklist interactions
  const [checklist, setChecklist] = useState({
    why: false,
    sentence: false,
    not: false,
    curiosity: false,
    icp: false,
    value: false,
    selective: false,
  });

  const toggleCheck = (key) =>
    setChecklist((prev) => ({ ...prev, [key]: !prev[key] }));

  // Internal Helper Component for the Checkbox items
  const CheckItem = ({ id, label }) => (
    <div
      onClick={() => toggleCheck(id)}
      className={`flex items-start gap-3 p-2 rounded cursor-pointer transition-all ${
        checklist[id] ? "bg-[#EAF4EF] opacity-60" : "hover:bg-stone-50"
      }`}
    >
      <div
        className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
          checklist[id] ? "bg-[#1F6F54] border-[#1F6F54]" : "border-stone-300"
        }`}
      >
        {checklist[id] && <CheckCircle2 className="w-3 h-3 text-white" />}
      </div>
      <span
        className={`text-sm ${
          checklist[id] ? "text-[#1F6F54] line-through" : "text-stone-700"
        }`}
      >
        {label}
      </span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header - Fairway Green */}
        <div className="bg-[#1F6F54] p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
          >
            <div className="bg-white/10 rounded-full p-1">
              <RotateCcw className="w-4 h-4 rotate-45" />
            </div>
          </button>

          <div className="flex items-center gap-2 text-amber-300 mb-2 text-xs font-bold tracking-widest uppercase">
            <Clock className="w-3 h-3" /> 11:00 AM — 12:00 PM
          </div>
          <h2 className="text-2xl font-bold tracking-tight mb-1">
            Coffee Meeting
          </h2>
          <p className="text-[#EAF4EF] text-sm opacity-90">
            Relationship & Fit Check • Foxborough
          </p>
        </div>

        {/* Sub-Header Context */}
        <div className="bg-[#EAF4EF] px-6 py-3 border-b border-[#1F6F54]/10 flex items-center justify-between">
          <div className="text-xs font-medium text-[#1F6F54]">
            Via <span className="font-bold">Mike Thyne</span>
          </div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-amber-600 flex items-center gap-1">
            <Star className="w-3 h-3 fill-amber-600" /> Exploratory Only
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-8">
          {/* Section 1: Mental Prep */}
          <section>
            <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#1F6F54]" /> Mental Prep
            </h3>
            <div className="space-y-1">
              <CheckItem
                id="why"
                label="Clear on why (fit discovery, not selling)"
              />
              <CheckItem
                id="sentence"
                label="Can explain what we do in 2 sentences"
              />
              <CheckItem
                id="not"
                label="Can explain what we do NOT do (no spray & pray)"
              />
              <CheckItem
                id="curiosity"
                label="Grounded in curiosity, not outcome"
              />
            </div>
          </section>

          {/* Section 2: Business Clarity */}
          <section>
            <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-500" /> Key Points
            </h3>
            <div className="grid grid-cols-1 gap-1">
              <CheckItem
                id="icp"
                label="Ideal Customer Profile ready (Titles + Type)"
              />
              <CheckItem
                id="value"
                label="One short success example (No deck)"
              />
              <CheckItem
                id="selective"
                label="Partnership Framing: 'We are being selective'"
              />
            </div>
          </section>

          {/* Section 3: Conversation Flow */}
          <section className="bg-stone-50 rounded-lg p-5 border border-stone-100">
            <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4">
              Conversation Anchor
            </h3>

            <div className="space-y-6 relative">
              {/* Vertical Line */}
              <div className="absolute left-0 top-2 bottom-2 w-0.5 bg-stone-200" />

              <div className="pl-4 relative">
                <div className="text-[10px] text-[#1F6F54] font-bold mb-1 uppercase">
                  Opening
                </div>
                <p className="text-sm text-stone-600 italic">
                  "Really appreciate you taking the time... This is very much a
                  get-to-know-each-other conversation."
                </p>
              </div>

              <div className="pl-4 relative">
                <div className="text-[10px] text-[#1F6F54] font-bold mb-1 uppercase">
                  The Core Question
                </div>
                <p className="text-sm text-stone-800 font-medium">
                  "When you look at setting up meetings and building pipeline...
                  where do you see the biggest gap right now?"
                </p>
              </div>

              <div className="pl-4 relative">
                <div className="text-[10px] text-[#1F6F54] font-bold mb-1 uppercase">
                  Soft Close
                </div>
                <p className="text-sm text-stone-600 italic">
                  "If there’s a natural next step, great. If not, still really
                  glad we connected."
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Post-Meeting */}
          <section className="opacity-60 grayscale">
            <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-stone-300" /> Post-Meeting
              (Unlocks 12:05pm)
            </h3>
            <div className="border border-dashed border-stone-300 rounded p-4 text-xs text-stone-500 text-center">
              Send thank you · Capture notes · Decide fit
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="bg-stone-50 p-4 border-t border-stone-200 text-center">
          <p className="text-xs font-serif italic text-stone-500">
            "The goal is clarity, not closure."
          </p>
        </div>
      </div>
    </div>
  );
};

export default CoffeeMeetingModal;
